import { Typo } from "@pepperstone/pepper-ui-kit";
import { createColumnHelper, FilterFn } from "@tanstack/react-table";
import dayjs from "dayjs";

import { formatCurrencyWithSymbol } from "../../../utils/currency";
import { BodyTypo } from "../../Common/BodyTypo";
import { HeaderTypo } from "../../Common/HeaderTypo";
import { FundedNotTradedColumnLabels, FundedNotTradedRow } from "../../types";

const columnHelper = createColumnHelper<FundedNotTradedRow>();

export const rebateAccountFilter: FilterFn<FundedNotTradedRow> = (
  row,
  columnId,
  value,
) => {
  if (columnId !== "rebateAccountID") return true;

  const rebateAccountID = row.original.rebateAccountID;
  if (!value) return true;

  const selectedIDs = value as string[];

  return selectedIDs.includes(rebateAccountID);
};

/**
 * Generates columns for the Client Funded Not Traded table.
 * @param {Object} options - Options for generating columns.
 * @param {FundedNotTradedColumnLabels} [options.translatedContent] - Translated column labels.
 * @param {string} options.language - Language code for formatting.
 * @param {boolean} [options.isBackoffice] - Flag indicating if backoffice view is enabled.
 * NOTE: name and email columns are removed as per request from BA/PM
 * @returns {import("@tanstack/react-table").ColumnDef<FundedNotTradedRow, any>[]} Array of column definitions.
 */
export const clientFundedNotTradedColumns = ({
  translatedContent,
  language,
}: {
  translatedContent?: FundedNotTradedColumnLabels;
  language: string;
  isBackoffice?: boolean;
}) => {
  const columns = [
    columnHelper.accessor("accountID", {
      header: () => <HeaderTypo text={translatedContent?.acc} />,
      cell: (info) => <BodyTypo text={`${info.getValue()}`} />,
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      enableGlobalFilter: true,
      size: 80,
    }),
    columnHelper.accessor("accountType", {
      header: () => <HeaderTypo text={translatedContent?.accType} />,
      cell: (info) => <BodyTypo text={`${info.getValue()}`} />,
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      enableGlobalFilter: false,
      size: 120,
    }),
    columnHelper.accessor("clientID", {
      header: () => <HeaderTypo text={translatedContent?.clientId} />,
      cell: (info) => <BodyTypo text={info.getValue()} />,
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      enableGlobalFilter: true,
      size: 120,
    }),
    columnHelper.accessor("depositedAt", {
      header: () => <HeaderTypo text={translatedContent?.depositDate} />,
      cell: (info) => {
        const { depositedAt } = info.row.original;
        return (
          <BodyTypo
            text={depositedAt ? dayjs(depositedAt).format("DD MMM YYYY") : "-"}
          />
        );
      },
      enableGlobalFilter: false,
      sortingFn: "alphanumeric",
      size: 180,
    }),
    columnHelper.accessor("depositedAmount", {
      header: () => <HeaderTypo text={translatedContent?.depositAmount} />,
      cell: (info) => {
        const { depositedAmount, currencyCode } = info.row.original;
        return (
          <Typo
            elem="p"
            type="body"
            size="md"
            responsiveClasses="ps-line-clamp-1"
          >
            {formatCurrencyWithSymbol(
              depositedAmount,
              currencyCode,
              language,
              false,
              false,
            )}
          </Typo>
        );
      },
      enableGlobalFilter: false,
      sortingFn: "alphanumeric",
      size: 120,
    }),
    columnHelper.accessor("rebateAccountID", {
      header: () => <HeaderTypo text={translatedContent?.rebateAccount} />,
      cell: (info) => <BodyTypo text={`${info.getValue()}`} />,
      enableGlobalFilter: false,
      filterFn: rebateAccountFilter,
      enableColumnFilter: false,
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      size: 120,
    }),
  ];

  return columns;
};
